import type {
  AgentConnectionStatus,
  ChatAttachment,
  ChatMessage,
  ChatSession,
  CustomFolder,
  ModelInfo,
  SessionFolder,
  UiRender,
} from '@paradigm/terminal-core';

import { createSafeContext } from '../context/createSafeContext';

export interface AgentState {
  readonly status: AgentConnectionStatus;
  readonly errorMessage: string | null;
  /**
   * True when the terminal has everything it needs to show (vs. the login
   * screen): a gateway URL, plus a token for token-only hosts
   * (`supportsPairingRole === false`). Drives the top-level auth gate. Distinct
   * from `status`, which is the live socket state — `isPaired` persists across
   * reconnects and only flips on (re)pair / disconnect.
   */
  readonly isPaired: boolean;
  /** Messages for the active session — hydrated from per-session storage on switch. */
  readonly messages: readonly ChatMessage[];
  readonly agentUrl: string | null;
  /** Ephemeral "agent is doing something" indicator (Thinking / Calling tool / …). */
  readonly processing: {
    readonly label: string;
    readonly detail?: string;
  } | null;
  /** A generative-UI render that has taken over the full chat viewport, if any. */
  readonly fullTerminalUi: {
    readonly messageId: string;
    readonly ui: UiRender;
  } | null;
  /** All known sessions for the current account, persisted across reloads. */
  readonly sessions: readonly ChatSession[];
  /** User-created folders for grouping sessions. */
  readonly customFolders: readonly CustomFolder[];
  /** The session currently shown in the Chat tab. */
  readonly activeSessionId: string | null;
  /** Run ids the user explicitly cancelled via Stop (post-stop frames are dropped). */
  readonly cancelledRunIds: ReadonlySet<string>;
  /**
   * Models offered by the picker. Sourced ONLY from the gateway's
   * `/api/models` — there is deliberately no built-in fallback list, so this
   * is empty until the live catalog lands (see `modelsStatus`).
   */
  readonly models: readonly ModelInfo[];
  /**
   * Lifecycle of the live catalog fetch, so the composer can hold the picker's
   * slot with an honest placeholder instead of popping in:
   *  - `idle`        — not fetched yet (not authenticated / no account)
   *  - `loading`     — request in flight, or waiting on a retry
   *  - `ready`       — `models` is the gateway's catalog
   *  - `unavailable` — retries exhausted; the gateway never answered
   */
  readonly modelsStatus: 'idle' | 'loading' | 'ready' | 'unavailable';
  /**
   * Model of the ACTIVE session, as reported by the gateway (its session
   * `modelOverride` is the single source of truth — nothing is persisted
   * UI-side). Read silently via `sessions.describe` on hydrate / session
   * switch / reconnect; flipped optimistically on a picker pick and then
   * verified against the gateway (bounced back on mismatch). Fresh chats are
   * seeded with the last-used model (gateway default when there is no
   * previous session). `null` ⇒ not yet known (before the first read-back
   * resolves).
   */
  readonly selectedModelId: string | null;
  /**
   * Per-session model, keyed by session id — the UI-side truth for "what will
   * this chat run", mirroring each session's gateway `modelOverride`. In
   * memory only; a reload rebuilds it from the gateway.
   *
   * `selectedModelId` above is DERIVED from this (see the provider's
   * precedence chain) rather than stored independently. That is deliberate:
   * when the two were separate fields, every bug in this area was a call site
   * that updated one and not the other, or guarded the update on the wrong
   * thing. A late read-back now writes into its own session's slot, where it
   * is harmless, instead of racing a single shared scalar.
   */
  readonly sessionModels: Readonly<Record<string, string>>;
  /**
   * A pick made while no session exists yet (fresh install: the picker is
   * live but `activeSessionId` is null, so there is no slot to write). Ranks
   * ABOVE any resolved default in the derivation, which is what stops a
   * read-back from silently reverting it. Cleared once `sendMessage` mints the
   * session and seeds it.
   */
  readonly pendingModelId: string | null;
  /**
   * The gateway MAIN session's model, used only to show something sensible
   * before any chat exists. Display-only and ranked below `pendingModelId`:
   * it is the agent's shared session, so another surface (CLI, cron) may have
   * set it, and it must never become what a real chat runs.
   */
  readonly mainSessionModelId: string | null;
  /** The gateway's default model (`sessions.list` → `defaults`). */
  readonly gatewayDefaultModelId: string | null;
  /** Transient error surfaced briefly then auto-dismissed (e.g. "gateway unreachable"). */
  readonly transientError: string | null;
  /**
   * Run failures, keyed by the session that OWNS the failed run. Unlike
   * `transientError` these do NOT auto-dismiss: a run that fails while the
   * user is reading another chat (or looking away) must still be discoverable
   * when they come back, so the entry survives session switches and clears
   * only when the user dismisses it or retries that chat.
   *
   * Deliberately NOT part of the transcript: infrastructure failures are not
   * conversation content and must not persist to localStorage or sync as
   * messages. In-memory only — a reload starts clean.
   */
  readonly sessionErrors: Readonly<Record<string, string>>;
}

export interface AgentActions {
  /**
   * Send a chat message. Optional `context` is per-message hidden context
   * appended to the gateway payload (like `systemPreamble`, but per-call) —
   * it does NOT appear in the chat bubble. Used e.g. to attach a `/analyze`d
   * trade's structured data without cluttering the visible transcript.
   * Optional `attachments` are image attachments (base64 `content` +
   * metadata); the payload is sent to the gateway, the transcript keeps only
   * metadata + thumbnail.
   */
  readonly sendMessage: (
    content: string,
    opts?: {
      readonly context?: string;
      readonly attachments?: readonly ChatAttachment[];
    },
  ) => void;
  /**
   * Open (or reopen) the gateway connection. An optional `token` is a fresh
   * pairing token (e.g. from the setup form): it's persisted to the token
   * store — replacing any stale device token — before the handshake, so the
   * connect frame carries it as `auth.token`.
   */
  readonly connect: (url?: string, token?: string) => void;
  readonly disconnect: () => void;
  /** Clear messages in the *active* session only. */
  readonly clearMessages: () => void;
  readonly respondToApproval: (approvalId: string, approved: boolean) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  readonly requestPairing: () => Promise<string>;
  readonly sendUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
  readonly dismissFullTerminalUi: () => void;
  /**
   * Clear the recorded run failure for a session (defaults to the active one).
   * The user acknowledging the error is what retires it — there is no timer.
   */
  readonly dismissSessionError: (sessionId?: string) => void;
  readonly createSession: (opts: {
    readonly folder: SessionFolder;
    readonly isTemporary?: boolean;
  }) => string;
  readonly switchSession: (sessionId: string) => void;
  readonly renameSession: (sessionId: string, title: string) => void;
  readonly pinSession: (sessionId: string, pinned: boolean) => void;
  readonly deleteSession: (sessionId: string) => void;
  readonly setSessionFolder: (
    sessionId: string,
    customFolderId: string | undefined,
  ) => void;
  readonly createCustomFolder: (name: string) => string;
  readonly renameCustomFolder: (folderId: string, name: string) => void;
  readonly pinCustomFolder: (folderId: string, pinned: boolean) => void;
  readonly deleteCustomFolder: (folderId: string) => void;
  readonly stopProcessing: () => void;
  readonly setSelectedModel: (modelId: string) => void;
}

export const INITIAL_AGENT_STATE: AgentState = {
  status: 'disconnected',
  errorMessage: null,
  isPaired: false,
  messages: [],
  agentUrl: null,
  processing: null,
  fullTerminalUi: null,
  sessions: [],
  customFolders: [],
  activeSessionId: null,
  cancelledRunIds: new Set<string>(),
  models: [],
  modelsStatus: 'idle',
  selectedModelId: null,
  transientError: null,
  sessionErrors: {},
  sessionModels: {},
  pendingModelId: null,
  mainSessionModelId: null,
  gatewayDefaultModelId: null,
};

export const [useAgentState, AgentStateProvider] =
  createSafeContext<AgentState>('AgentState');

export const [useAgentActions, AgentActionsProvider] =
  createSafeContext<AgentActions>('AgentActions');

/**
 * Narrow, host-facing agent API — the ONLY agent surface re-exported from the
 * package's public entry (`useTerminalAgent`). Host `agentEffects` (see
 * `TerminalProps.agentEffects`) use it to drive the agent from host state:
 * e.g. a block-trade context pump calling `request(...)`, or a pending-message
 * drain calling `sendMessage(...)` once `status === 'authenticated'`.
 *
 * The full `AgentActions` (28 mutating methods) and `AgentState` stay internal
 * so they don't become a published semver contract.
 */
export interface TerminalAgentApi {
  /** Current connection/auth status — gate effects on `'authenticated'`. */
  readonly status: AgentConnectionStatus;
  /**
   * Live transcript of the ACTIVE session, in order. Read-only: lets a host
   * node react to agent replies it solicited via `sendMessage` (e.g. Paradex's
   * subkey setup flow prompts the agent to answer with a sentinel-keyed JSON
   * block, then scans assistant messages for it). A message that is still
   * streaming carries `isStreaming: true` and updates in place — wait for it
   * to flip off before treating the content as final.
   */
  readonly messages: readonly ChatMessage[];
  /**
   * Send a chat message as the user. The host preamble (if any) is applied,
   * and optional `context` is per-message hidden context appended to the
   * gateway payload only — not shown in the chat bubble. Use it to attach
   * structured data (e.g. a `/analyze`d trade) without cluttering the chat.
   * Optional `attachments` are image attachments (see `ChatAttachment`).
   */
  readonly sendMessage: (
    content: string,
    opts?: {
      readonly context?: string;
      readonly attachments?: readonly ChatAttachment[];
    },
  ) => void;
  /** Issue a raw gateway RPC (e.g. `set_block_trade_context`). */
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
}

export function useTerminalAgent(): TerminalAgentApi {
  const { sendMessage, request } = useAgentActions();
  const { status, messages } = useAgentState();
  return { status, messages, sendMessage, request };
}

/**
 * Host-facing AUTH seam — login state plus programmatic sign-in/out, so an
 * enclosing app can drive pairing itself instead of (or alongside) the
 * terminal's built-in login screen. The terminal owns its own credential
 * storage; this just lets the host push credentials in and read the result.
 *
 * Two ways a host signs the terminal in:
 *  - Declarative: pass `gatewayUrl` + `fallbackPairingToken` (or `resolveUrl`)
 *    in `TerminalConfig`; the terminal auto-pairs from them on mount.
 *  - Imperative: render a node in `agentEffects` that fetches credentials
 *    (e.g. after the host's own OAuth) and calls `login(url, token)`.
 *
 * `isLoggedIn` mirrors the internal pairing state; `logout()` unpairs (drops
 * the stored token) and returns to the login screen.
 */
export interface TerminalAuthApi {
  readonly isLoggedIn: boolean;
  readonly login: (url: string, token?: string) => void;
  readonly logout: () => void;
}

export function useTerminalAuth(): TerminalAuthApi {
  const { connect, disconnect } = useAgentActions();
  const { isPaired } = useAgentState();
  return {
    isLoggedIn: isPaired,
    login: (url: string, token?: string) => connect(url, token),
    logout: () => disconnect(),
  };
}
